import type { TouchData, TrackingCookie } from '../types/tracking.js';
import { MAX_COOKIE_BYTES } from '../config.js';
import { logger } from '../utils/logger.js';

/**
 * Taille de la valeur ENCODÉE du cookie (ce que nginx mesure réellement), en octets.
 */
export function encodedSize(json: string): number {
  return encodeURIComponent(json).length;
}

const CLICK_ID_FIELDS: (keyof TouchData)[] = ['gclid', 'msclkid', 'li_fat_id', 'fbclid'];

/** Vide les click-ids d'une touche (les plus gros contributeurs de poids). */
function clearClickIds(touch: TouchData): void {
  for (const field of CLICK_ID_FIELDS) {
    touch[field] = '';
  }
}

/**
 * Garde-fou de taille à l'écriture du cookie.
 *
 * Renvoie la chaîne JSON à écrire. En temps normal (cookie sous le budget) c'est
 * un no-op : la donnée est renvoyée telle quelle. Sur un cas pathologique
 * (ex. fbclid géant sur first ET last touch), dégrade gracieusement en sacrifiant
 * les données les moins critiques dans un ordre défini, jusqu'à repasser sous le
 * budget, puis logge précisément ce qui a été retiré.
 *
 * Ne mute JAMAIS l'objet reçu : opère sur un clone profond (les builders
 * HubSpot/Amplitude du même page load doivent garder les données complètes).
 * Ne sacrifie jamais l'identité, l'amplitude_device_id, les compteurs de page
 * views ni les champs last_form_* : seuls les touches et click-ids sont dégradables.
 */
export function fitToCookieBudget(data: TrackingCookie): string {
  const json = JSON.stringify(data);
  if (encodedSize(json) <= MAX_COOKIE_BYTES) return json; // cas normal : no-op

  const clone: TrackingCookie = JSON.parse(JSON.stringify(data));
  const dropped: string[] = [];

  const isUnderBudget = (): boolean =>
    encodedSize(JSON.stringify(clone)) <= MAX_COOKIE_BYTES;

  // Échelle de dégradation, du moins critique au plus critique :
  // first-touch avant last-touch, click-ids avant l'objet touche entier.
  const steps: Array<{ label: string; apply: () => void }> = [
    { label: 'firstpage_view', apply: () => { delete clone.firstpage_view; } },
    { label: 'first_touch.click_ids', apply: () => { if (clone.first_touch) clearClickIds(clone.first_touch); } },
    { label: 'first_touch', apply: () => { delete clone.first_touch; } },
    { label: 'last_touch.click_ids', apply: () => { if (clone.last_touch) clearClickIds(clone.last_touch); } },
    { label: 'last_touch', apply: () => { delete clone.last_touch; } },
  ];

  for (const step of steps) {
    step.apply();
    dropped.push(step.label);
    if (isUnderBudget()) break;
  }

  const finalJson = JSON.stringify(clone);
  logger.error(
    `Cookie taleez_tracking trop volumineux, dégradation: ${dropped.join(', ')} (${encodedSize(finalJson)} octets)`,
  );
  return finalJson;
}
